import { useQuery } from "@apollo/client"
import { FloatingPortal, offset, size, useFloating, useDismiss, useInteractions } from "@floating-ui/react"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import twcn from "classnames"
import { AnimatePresence, motion } from "framer-motion"
import React, { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { graphql } from "@/api"
import { type ClientRole } from "@/api/graphql"
import DotSpinner from "@/common/components/dot_spinner"
import Scrollable from "@/common/components/scrollable"
import useMedia from "@/common/hooks/use_media"
import storageLocal from "@/common/storage"
import { type SizeApplyArgs } from "@/common/util"
import { mergeRefs } from "@/common/util/react"
import { CheckmarkLarge, ChevronDown, CloseLarge, InfoFill } from "@/skylticons"

const priceDoc = graphql(`
  query SignPrice($id: ID!, $examplerId: Int!, $count: Int, $signcode: String, $clientRole: ClientRole) {
    signPrice(id: $id, examplerId: $examplerId, count: $count, signcode: $signcode, clientRole: $clientRole) {
      count
      regularPrice
      totalPrice
      discountedPrice
      totalDiscountedPrice
      discountPercent
    }
  }
`)

const pricesDoc = graphql(`
  query SignPrices($id: ID!, $examplerId: Int!, $counts: [Int!]!, $signcode: String, $clientRole: ClientRole) {
    signPrices(id: $id, examplerId: $examplerId, counts: $counts, signcode: $signcode, clientRole: $clientRole) {
      count
      regularPrice
      totalPrice
      discountedPrice
      totalDiscountedPrice
      discountPercent
    }
  }
`)

interface OptionProps {
  children?: React.ReactNode
  className?: string
  selected?: boolean
  onSelected: () => void
}

const Option: React.FC<OptionProps> = ({ children, className, selected, onSelected }) => {
  const ref = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (selected) {
      setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" }), 1)
    }
  }, [selected])

  const customClassName = typeof className === "string" ? className : ""

  className = twcn(
    "relative mb-0 flex h-12 cursor-pointer scroll-mb-96 items-center border border-transparent pl-4 pr-12 text-base focus:border-primary lg:h-10 lg:scroll-m-28",
    {
      "hover:bg-white-3": !selected,
      "bg-blue-100 hover:bg-blue-200": selected,
    },
    customClassName
  )

  return (
    <li className={className} onClick={onSelected} ref={ref}>
      {children}
    </li>
  )
}

interface SelectProps<T> {
  id?: string
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean | undefined>>
  selected: T
  selectedLabel?: (value: T) => React.ReactNode
  customValue?: number
  setCustomValue: (value: number) => void
  label?: string
  name?: string
  className?: string
  disabled?: boolean
  businessRole?: boolean
  children?: React.ReactNode
}

const Select = <T,>({
  id,
  open,
  setOpen,
  selected,
  selectedLabel,
  label,
  name,
  className,
  disabled,
  children,
  customValue,
  businessRole,
  setCustomValue,
}: SelectProps<T>) => {
  const { t } = useTranslation()
  const desktop = useMedia(`(min-width: 1024px)`, true)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleOpen = (open: boolean) => {
    setOpen(open)

    if (!open) {
      buttonRef.current?.focus()
    }
  }

  const toggle = () => handleOpen(!open)

  const { x, y, strategy, refs, context } = useFloating({
    open,
    onOpenChange: handleOpen,
    middleware: desktop
      ? [
          offset(2),
          size({
            apply({ availableWidth, availableHeight, elements, rects }: SizeApplyArgs) {
              Object.assign(elements.floating.style, {
                maxWidth: `${availableWidth}px`,
                maxHeight: `${Math.min(availableHeight - 10, 384)}px`,
                width: `${rects.reference.width}px`,
              })
            },
          }),
        ]
      : [],
  })

  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  id = id || "example-qty-select"

  const submit = (e: React.FormEvent) => {
    e.preventDefault()

    if (customValue && customValue > 0) {
      handleOpen(false)
    }
  }

  const ssrDisabled = useSSRDisabled()

  return (
    <div className={twcn("relative", { disabled: disabled }, className)}>
      <label htmlFor={id} className="w-full">
        {label && <div className="mb-1 text-sm font-semibold">{label}</div>}
        <div className="relative w-full">
          <button
            onClick={toggle}
            id={id}
            name={name}
            aria-invalid="false"
            ref={mergeRefs(buttonRef, refs.setReference)}
            type="button"
            disabled={disabled || ssrDisabled}
            className={twcn(
              "qty-dropdown inline-flex h-12 w-full items-center truncate rounded border pl-4 pr-12 text-base outline-none transition placeholder:text-[rgb(117,117,117)] focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-50 lg:h-10",
              {
                "hover:border-ink-50": !disabled && !open,
                "cursor-not-allowed bg-gray-50": disabled || ssrDisabled,
              }
            )}
            {...getReferenceProps()}
          >
            <>
              <div className="w-full truncate">
                {selectedLabel ? selectedLabel(selected) : <>{typeof selected === "string" ? selected : ""}</>}
              </div>
              <div className="absolute inset-y-0 right-3 flex items-center justify-center text-ink-100">
                <ChevronDown className={twcn("h-6 w-6 transition-transform", { "rotate-180": open })} />
              </div>
            </>
          </button>
        </div>
      </label>

      <AnimatePresence>
        {open && (
          <FloatingPortal>
            {desktop ? (
              <div
                className="z-20 flex w-full origin-top flex-col rounded-lg border border-white-10 bg-white shadow-custom-sm focus:outline-none lg:min-h-[384px]"
                ref={refs.setFloating}
                style={{
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                  width: "max-content",
                }}
                {...getFloatingProps()}
              >
                <form className="flex-shrink-0 flex-grow-0 p-3" onSubmit={submit}>
                  <input
                    className="qty-input form-control !mb-0"
                    type="number"
                    placeholder={t("examples.custom_qty")}
                    min={0}
                    max={10000000}
                    inputMode="numeric"
                    value={customValue?.toString() || ""}
                    onChange={e => setCustomValue(parseInt(e.target.value))}
                  />
                </form>

                <div
                  className={twcn("visible-scrollbar flex-grow border-t border-white-10", {
                    "h-[264px]": businessRole,
                    "h-80": !businessRole,
                  })}
                >
                  <Scrollable>{children}</Scrollable>
                </div>

                {businessRole && (
                  <div className="flex h-14 flex-shrink-0 flex-grow-0 items-center justify-between border-t border-white-10 px-4 text-base">
                    {t("examples.quote_suggest")}
                    <a href={t("route.quote")} className="font-medium text-primary underline">
                      {t("menu.request_quote")}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="fixed inset-0 z-40 bg-ink-100 bg-opacity-60"
                />
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ duration: 0.15 }}
                  className="minimatic-mobile-dropdown fixed inset-x-0 bottom-0 top-0 z-50 mt-12 flex flex-col rounded-t-2xl bg-white shadow-custom-sm ring-0 focus:outline-none"
                  ref={refs.setFloating}
                  {...getFloatingProps()}
                >
                  <form className="flex flex-shrink-0 flex-grow-0 items-center gap-3 p-4" onSubmit={submit}>
                    <input
                      className="form-control !mb-0"
                      type="number"
                      placeholder={t("examples.custom_qty")}
                      min={0}
                      max={10000000}
                      inputMode="numeric"
                      value={customValue?.toString() || ""}
                      onChange={e => setCustomValue(parseInt(e.target.value))}
                    />
                    <button
                      onClick={toggle}
                      className="flex h-12 w-12 flex-shrink-0 items-center justify-center"
                      type="button"
                    >
                      <CloseLarge className="h-6 w-6" />
                    </button>
                  </form>
                  <div className="visible-scrollbar flex-grow border-t border-white-10">
                    <Scrollable>{children}</Scrollable>
                  </div>
                  {businessRole && (
                    <div className="flex flex-shrink-0 flex-grow-0 items-center justify-between gap-3 border-t border-white-10 px-3 pb-6 pt-4 text-sm">
                      {t("examples.quote_suggest")}
                      <a href={t("route.quote")} className="font-medium text-primary underline">
                        {t("menu.request_quote")}
                      </a>
                    </div>
                  )}
                </motion.div>
              </>
            )}
          </FloatingPortal>
        )}
      </AnimatePresence>
    </div>
  )
}

interface SignQuantityProps {
  id: string
  clientRole: ClientRole
  price?: string | null
  minimatic?: boolean
  updateCount?: (count: number) => void
  fixedPrice?: boolean
  examplerId: number
  signcode?: string
}

const SignQuantity: React.FC<SignQuantityProps> = ({
  id,
  clientRole,
  price,
  minimatic,
  updateCount,
  fixedPrice,
  examplerId,
  signcode,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [count, setCount] = useState(1)
  const [customCount, setCustomCount] = useState<number>()
  const [minimaticNotifEnabled, setMinimaticNotifEnabled] = useState(
    storageLocal.getItem("example_quantity_notification") !== "true"
  )

  const showQuantityNotification = minimatic && minimaticNotifEnabled && count > 1

  const closeQuantityNotification = () => {
    setMinimaticNotifEnabled(false)
    storageLocal.setItem("example_quantity_notification", "true")
  }

  const counts = [1, 2, 3, 4, 5, 10, 20, 30, 40, 50, 100, 150, 200, 250, 300, 500, 1000, 2000, 3000]

  const handleCount = (count: number) => {
    if (updateCount) updateCount(count)
    setCount(count)
    setOpen(false)

    if (counts.includes(count)) {
      setCustomCount(undefined)
    }
  }

  const handleCustomCount = (count: number) => {
    if (Number.isNaN(count)) {
      if (updateCount) updateCount(1)
      setCount(1)
      setCustomCount(undefined)
      return
    }

    if (count > 0) {
      if (updateCount) updateCount(count)
      setCount(count)
    }
    setCustomCount(count)
  }

  const priceResults = useQuery(priceDoc, {
    variables: { id, examplerId, count, signcode, clientRole },
    skip: counts.includes(count) && !signcode,
  })
  const discountResults = useQuery(pricesDoc, { variables: { id, examplerId, counts, signcode, clientRole } })
  const prices = discountResults.data?.signPrices || []
  const customPrice = priceResults.data?.signPrice || priceResults.previousData?.signPrice

  let match: (typeof prices)[0] | undefined

  if (prices.length > 0 && count > 1) {
    match = prices.find(p => p.count === count)
  }

  let actualPrice: React.ReactNode = customPrice?.regularPrice || price
  let regularPrice: React.ReactNode | undefined
  let discountPercent: React.ReactNode | undefined
  let perItem: React.ReactNode = customPrice?.regularPrice || price

  if (count > 1) {
    if (match) {
      actualPrice = match.totalDiscountedPrice || match.totalPrice
      regularPrice = match.totalPrice
      perItem = match.discountedPrice || match.regularPrice
      discountPercent = match.discountPercent
    } else if (customPrice) {
      actualPrice = customPrice.totalDiscountedPrice || customPrice.totalPrice
      regularPrice = customPrice.totalPrice
      perItem = customPrice.discountedPrice || customPrice.regularPrice
      discountPercent = customPrice.discountPercent
    } else {
      actualPrice = <DotSpinner />
      perItem = <DotSpinner />

      if (priceResults.loading) {
        discountPercent = "-"
      }
    }
  }

  const countOptions = [...counts]

  if (customCount && customCount > 0 && !countOptions.includes(customCount)) {
    countOptions.push(customCount)
    countOptions.sort((a, b) => a - b)
  }

  return (
    <>
      <Select
        label={t("examples.select_qty")}
        open={open}
        setOpen={setOpen}
        selected={count}
        disabled={discountResults.loading}
        customValue={customCount}
        setCustomValue={handleCustomCount}
        businessRole={clientRole === "business"}
        selectedLabel={v => (
          <div className="flex w-full items-center justify-between">
            <span>{v}</span>
            <div className="flex items-center gap-2">
              {!fixedPrice && (
                <div className="ml-3 flex min-h-[24px] max-w-none items-center whitespace-normal rounded bg-blue-200 px-2 py-[3px] text-xs font-semibold leading-normal text-blue-600 sm:whitespace-nowrap">
                  {discountPercent
                    ? t("order.discount_badge", { discount: `${discountPercent}%` })
                    : t("examples.qty_badge")}
                </div>
              )}
              <div className="flex items-center">
                {perItem} {t("order.price_per_item")}
              </div>
            </div>
          </div>
        )}
      >
        {countOptions.map(countOpt => {
          let optMatch = prices.find(p => p.count === countOpt)
          const custom = countOpt === customCount

          if (!optMatch && custom) {
            optMatch = customPrice
          }

          return (
            <Option
              key={countOpt}
              className={`flex w-full items-center qty-${countOpt}`}
              selected={countOpt === count}
              onSelected={() => handleCount(countOpt)}
            >
              <div className="flex-1">{countOpt}</div>
              <div className="flex items-center gap-2">
                {optMatch && optMatch.discountPercent > 0 && (
                  <div className="discount-badge rounded bg-blue-200 px-2 py-[3px] text-xs font-semibold leading-normal text-blue-600">
                    {t("order.discount_badge", { discount: `${optMatch.discountPercent}%` })}
                  </div>
                )}
                <div>
                  {custom && !optMatch && <DotSpinner />}
                  {optMatch && `${optMatch?.discountedPrice || optMatch?.regularPrice} ${t("order.price_per_item")}`}
                </div>
              </div>

              {countOpt === count && (
                <div className="absolute inset-y-0 right-3 flex items-center justify-center text-aqua-500">
                  <CheckmarkLarge className="h-6 w-6" />
                </div>
              )}
            </Option>
          )
        })}
      </Select>
      {showQuantityNotification && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-info-bg p-3 lg:mt-2">
          <InfoFill className="h-6 w-6 flex-shrink-0 text-info" />
          <div className="flex-1">{t("examples.minimatic_text_qty")}</div>
          <button
            onClick={closeQuantityNotification}
            className="button button-large button-subtle icon-only -mr-3 -mt-3 mb-0"
            type="button"
          >
            <CloseLarge className="h-6 w-6" />
          </button>
        </div>
      )}
      <div className="example-price heading-four mb-0 mt-4 flex gap-1 lg:mt-6">
        <span className="total-amount flex h-6 items-center">{actualPrice}</span>
        {regularPrice !== actualPrice && <span className="font-normal text-ink-50 line-through">{regularPrice}</span>}
      </div>
      <p className="mb-4 text-xs leading-normal">
        {t("article.price")} {clientRole === "business" ? t("order.exc_vat") : t("order.inc_vat")}
      </p>
    </>
  )
}

export default SignQuantity
